/**
 * LabIntegrationAdmin.tsx
 *
 * Reusable Admin → Integrations page for a paid lab integration (SiPhox,
 * BodySpec, …). Configures the per-account billing mode + customer price and
 * tracks the month-end reconciliation. The customer price is floored by the
 * super-admin "minimum customer price" guidance rule set in Prospera-Corp-App
 * (returned as `settings.minCustomerPrice`); the input + Save are blocked below
 * it, and the backend enforces the same floor.
 *
 * Vendor API credentials are per-practice and live in the Prospera-Corp-App
 * super-admin Module Settings (module_account_settings.api_credentials), so this
 * page doesn't edit them — it only notes where they're managed.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): prices, checkout mode, billing
 * mode and covered memberships show read-only (an AdminLockedFieldset; the
 * billing mode as its label, the checkout checkbox inert), with no Save, no
 * Join (enrollment), no banking update and no membership change. Banking
 * stays last-4; the item search and the reconciliation still read. Nothing
 * says why.
 */
import { memo, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  getEnabledIntegrations,
  getIntegrationSettings,
  updateIntegrationSettings,
  getIntegrationReconciliation,
  getIntegrationOnboarding,
  getIntegrationBanking,
  type IntegrationSettingsItem,
} from '@/services/integrations.service';
import { listTemplates } from '@/services/memberships.service';
import { AxiomOnboardingModal } from '@/pages/admin/AxiomOnboardingModal';
import { AxiomBankingModal } from '@/pages/admin/AxiomBankingModal';

const BILLING_MODE_LABELS: Record<string, string> = {
  charge_patient: 'Charge the patient',
  log_for_settlement: 'Log & settle monthly',
};
const usd = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export interface LabIntegrationAdminProps {
  integrationKey: string;
  title: string;
  subtitle: string;
  /** Vendor label used in the reconciliation copy ("Owed to …"). */
  vendorName: string;
  /** Require clinic enrollment (Axiom Connect) before pricing is shown. */
  requiresOnboarding?: boolean;
}

/**
 * One item's price input, memoized.
 *
 * The list is rendered in full, and every row is a controlled input reading one
 * shared map — so each keystroke re-rendered all of them. At Partell's 371
 * items the settings modal locked up while typing. Memoizing means a keystroke
 * re-renders the row it happened in, and the functional setState keeps this
 * component's props stable so the memo actually holds.
 */
const PriceRow = memo(function PriceRow({
  item,
  value,
  onChange,
}: {
  item: IntegrationSettingsItem;
  value: string;
  onChange: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}) {
  const below = value.trim() !== '' && item.minPrice > 0 && Number(value) < item.minPrice;

  // A Prospera-set fee (the shipping fee): shown, never editable here.
  if (item.priceLocked) {
    return (
      <div className="flex items-center justify-between gap-4">
        <span className="min-w-0 flex-1 text-sm text-slate-700">{item.name}</span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="w-32 text-right text-sm text-slate-700" aria-label={`${item.name} price`}>
            {usd(item.customerPrice)}
            <span className="block text-[11px] text-slate-400">Set by Prospera</span>
          </span>
          {item.minPrice > 0 && <span className="w-32 shrink-0" aria-hidden="true" />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <span className="min-w-0 flex-1 text-sm text-slate-700">{item.name}</span>
      <div className="flex shrink-0 items-center gap-2">
        <div className="flex w-32 items-stretch">
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-100 px-2 text-sm text-slate-500">$</span>
          <Input
            type="number"
            min={item.minPrice > 0 ? String(item.minPrice) : '0'}
            step="0.01"
            value={value}
            aria-label={`${item.name} price`}
            onChange={(e) => {
              const next = e.target.value;
              onChange((p) => ({ ...p, [item.integrationProductId]: next }));
            }}
            className="rounded-l-none text-right"
          />
        </div>
        {/* Fixed width keeps the price inputs column-aligned across rows. The
            column is COGS, not a floor label — "min $235.00" read as a price
            floor, which it is not. */}
        {item.minPrice > 0 && (
          <span className={`w-32 shrink-0 whitespace-nowrap text-center text-[11px] ${below ? 'text-red-600' : 'text-slate-400'}`}>{usd(item.minPrice)}</span>
        )}
      </div>
    </div>
  );
});

export function LabIntegrationAdmin({ integrationKey: KEY, title, subtitle, vendorName, requiresOnboarding }: LabIntegrationAdminProps) {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data: integrations = [], isLoading: enabledLoading } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 60_000,
  });
  const enabled = useMemo(() => integrations.find((i) => i.key === KEY), [integrations, KEY]);

  const { data: settings } = useQuery({
    queryKey: ['integration-settings', KEY],
    queryFn: () => getIntegrationSettings(KEY),
    enabled: !!enabled,
    staleTime: 60_000,
  });

  const { data: recon, isLoading: reconLoading } = useQuery({
    queryKey: ['integration-reconciliation', KEY],
    queryFn: () => getIntegrationReconciliation(KEY, 6),
    enabled: !!enabled,
    staleTime: 60_000,
  });

  // ONE ENROLLMENT, ONE CACHE ENTRY. Pens and vials are two tabs over a single
  // clinic enrollment — the API stores and reads it under the same record for
  // both keys. Caching it per integration key meant completing it on one tab
  // left the other still holding `onboarded: false`, so the sibling kept
  // offering "Join Axiom Connect" and a practice would fill the wizard twice
  // for an enrollment it already had. Keying both on the family collapses that:
  // onboarding on either tab updates both, instantly.
  const onboardingScope = KEY === 'axiom_vials' ? 'axiom' : KEY;

  // Clinic enrollment gate (Axiom Connect). Pricing is hidden until onboarded.
  const { data: onboarding, isLoading: onboardingLoading } = useQuery({
    queryKey: ['integration-onboarding', onboardingScope],
    queryFn: () => getIntegrationOnboarding(onboardingScope),
    enabled: !!enabled && !!requiresOnboarding,
    staleTime: 60_000,
  });
  const [onboardOpen, setOnboardOpen] = useState(false);

  // Stored ACH details, last-4 only. Same gate as the enrollment: an
  // integration that doesn't bill by direct debit has no banking to show.
  const { data: banking } = useQuery({
    // Same enrollment record as above (bankingShow resolves it through the
    // family's onboarding module), so the same shared scope — otherwise the
    // vials tab shows "No bank account on file" for an account that has one.
    queryKey: ['integration-banking', onboardingScope],
    queryFn: () => getIntegrationBanking(onboardingScope),
    enabled: !!enabled && !!requiresOnboarding && !!onboarding?.onboarded,
  });
  const [bankingOpen, setBankingOpen] = useState(false);

  const saveMode = useMutation({
    mutationFn: (billingMode: string) => updateIntegrationSettings(KEY, { billingMode }),
    onSuccess: () => { toast.success('Billing mode updated.'); qc.invalidateQueries({ queryKey: ['integration-settings', KEY] }); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update billing mode.')),
  });

  const [priceInput, setPriceInput] = useState('');
  useEffect(() => { if (settings) setPriceInput(String(settings.price)); }, [settings]);

  const savePrice = useMutation({
    mutationFn: (price: number) => updateIntegrationSettings(KEY, { price }),
    onSuccess: () => {
      toast.success('Price updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
      qc.invalidateQueries({ queryKey: ['integrations', 'enabled'] });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update price.')),
  });

  const minPrice = settings?.minCustomerPrice ?? 0;
  const belowMin = settings != null && priceInput.trim() !== '' && minPrice > 0 && Number(priceInput) < minPrice;
  const priceDirty = settings != null && priceInput.trim() !== '' && Number(priceInput) !== settings.price;

  // Per-item pricing: whenever the integration has catalog items (one or many),
  // edit the per-account customer price per item. Only integrations with no
  // catalog item at all fall back to the legacy single global-price card.
  const items = useMemo(() => settings?.items ?? [], [settings]);
  const hasItems = items.length > 0;
  const [itemPrices, setItemPrices] = useState<Record<number, string>>({});
  useEffect(() => {
    setItemPrices(Object.fromEntries((settings?.items ?? []).map((it) => [it.integrationProductId, String(it.customerPrice)])));
  }, [settings]);

  /** Filters the list only — saving always sends every item. */
  const [itemSearch, setItemSearch] = useState('');
  const visibleItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();

    return q === '' ? items : items.filter((it) => it.name.toLowerCase().includes(q));
  }, [items, itemSearch]);

  const saveItems = useMutation({
    mutationFn: (payloadItems: Array<{ integrationProductId: number; customerPrice: number }>) =>
      updateIntegrationSettings(KEY, { items: payloadItems }),
    onSuccess: () => {
      toast.success('Item prices updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
      qc.invalidateQueries({ queryKey: ['integrations', 'enabled'] });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update item prices.')),
  });

  // Per-integration checkout mode (soft vs payment-enforced).
  const [enforced, setEnforced] = useState(false);
  useEffect(() => { if (settings) setEnforced(!!settings.paymentEnforced); }, [settings]);
  const saveEnforced = useMutation({
    mutationFn: (v: boolean) => updateIntegrationSettings(KEY, { paymentEnforced: v }),
    onSuccess: () => {
      toast.success('Checkout mode updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
      qc.invalidateQueries({ queryKey: ['integrations', 'enabled'] });
    },
    onError: (err) => { toast.error(getErrorMessage(err, 'Failed to update checkout mode.')); if (settings) setEnforced(!!settings.paymentEnforced); },
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['membership-templates'],
    queryFn: () => listTemplates(),
    enabled: !!enabled,
    staleTime: 5 * 60_000,
  });

  const membershipOptions = useMemo(
    () => templates.filter((t) => t.active).map((t) => ({ value: String(t.templateId), label: t.title })),
    [templates],
  );

  const [entitledLocal, setEntitledLocal] = useState<string[]>([]);
  useEffect(() => {
    if (settings) setEntitledLocal((settings.entitledMembershipIds ?? []).map(String));
  }, [settings]);

  const saveEntitled = useMutation({
    mutationFn: (ids: number[]) => updateIntegrationSettings(KEY, { entitledMembershipIds: ids }),
    onSuccess: () => {
      toast.success('Covered memberships updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to update memberships.'));
      if (settings) setEntitledLocal((settings.entitledMembershipIds ?? []).map(String));
    },
  });

  const billingOptions = useMemo(
    () => (settings?.billingModes ?? []).map((m) => ({ value: m, label: BILLING_MODE_LABELS[m] ?? m })),
    [settings],
  );

  const totals = useMemo(() => {
    const months = recon?.months ?? [];
    return {
      owed: months.reduce((s, m) => s + m.owedToVendor, 0),
      collected: months.reduce((s, m) => s + m.collected, 0),
      visits: months.reduce((s, m) => s + m.visits, 0),
    };
  }, [recon]);

  if (enabledLoading) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  if (!enabled) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={title} subtitle={subtitle} />
        <Card>
          <CardBody>
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-400" />
              <p className="text-sm text-slate-600">
                {title} is not enabled for this account. Please contact your Prospera account team to add this
                feature to your account.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Enrollment gate: until the clinic completes Axiom Connect onboarding, show
  // only the "Join" CTA — no pricing/billing/reconciliation.
  if (requiresOnboarding && !onboardingLoading && !onboarding?.onboarded) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={title} subtitle={subtitle} />
        <Card>
          <CardBody>
            <div className="flex flex-col items-start gap-5 py-1 sm:flex-row sm:items-center">
              <div className="flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-teal-700">Pharmacy &amp; supply network</p>
                <h3 className="mt-1.5 text-[17px] font-semibold text-slate-900">Order compounded cartridges through {vendorName} Connect</h3>
                {/* Locked: the enroll invitation, its button and wizard go. */}
                {!locked && (
                  <p className="mt-1.5 text-sm text-slate-500">
                    Enroll your clinic to order compounded cartridges with automatic ACH billing. Setup takes about three minutes.
                  </p>
                )}
              </div>
              {!locked && (
                <button
                  type="button"
                  onClick={() => setOnboardOpen(true)}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800"
                >
                  Join {vendorName} Connect <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
            {!locked && (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5" /> Pricing unlocks once enrollment is complete.
              </p>
            )}
          </CardBody>
        </Card>

        {!locked && (
          <AxiomOnboardingModal
            integrationKey={onboardingScope}
            vendorName={vendorName}
            open={onboardOpen}
            onClose={() => setOnboardOpen(false)}
            onCompleted={() => {
              setOnboardOpen(false);
              toast.success('Enrollment submitted. You can now set your prices.');
              qc.invalidateQueries({ queryKey: ['integration-onboarding', onboardingScope] });
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={title} subtitle={subtitle} />

      {/* Price — per item when the integration has catalog items, single otherwise */}
      {hasItems ? (
        <Card>
          <CardBody>
            <h3 className="text-sm font-semibold text-slate-900">{items.length > 1 ? 'Item prices' : 'Price'}</h3>
            <p className="text-[11px] text-slate-400 mb-3">
              Customer (retail) price per item.{!locked && ' Each must be at or above the minimum Prospera set.'}
            </p>
            <div className="flex items-center justify-between gap-4 pb-1">
              <span className="min-w-0 flex-1" aria-hidden="true" />
              <div className="flex shrink-0 items-center gap-2">
                <span className="w-32 text-center text-sm font-semibold text-slate-500 whitespace-nowrap">
                  Cost to Patient
                </span>
                {items.some((it) => it.minPrice > 0) && (
                  <span className="w-32 shrink-0 text-center text-sm font-semibold text-slate-500 whitespace-nowrap">
                    COGS
                  </span>
                )}
              </div>
            </div>
            {items.length > 12 && (
              <Input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder={`Search ${items.length} items…`}
                className="mb-2 max-w-xs"
              />
            )}

            {/* The item search above stays outside the lock: it only filters. */}
            <AdminLockedFieldset>
              <div className="space-y-2">
                {visibleItems.map((it) => (
                  <PriceRow
                    key={it.integrationProductId}
                    item={it}
                    value={itemPrices[it.integrationProductId] ?? ''}
                    onChange={setItemPrices}
                  />
                ))}
                {visibleItems.length === 0 && (
                  <p className="py-2 text-xs text-slate-500">No items match “{itemSearch}”.</p>
                )}
              </div>
            </AdminLockedFieldset>
            {!locked && (
              <Button
                size="sm"
                className="mt-3"
                disabled={saveItems.isPending || items.some((it) => {
                  if (it.priceLocked) return false;
                  const v = itemPrices[it.integrationProductId] ?? '';
                  return v.trim() === '' || (it.minPrice > 0 && Number(v) < it.minPrice);
                })}
                // Locked (Prospera-set) prices are never sent: not the location's to save.
                onClick={() => saveItems.mutate(items.filter((it) => !it.priceLocked).map((it) => ({
                  integrationProductId: it.integrationProductId,
                  customerPrice: Number(itemPrices[it.integrationProductId]),
                })))}
              >
                {saveItems.isPending ? 'Saving…' : 'Save prices'}
              </Button>
            )}
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <h3 className="text-sm font-semibold text-slate-900">Price</h3>
            <p className="text-[11px] text-slate-400 mb-2">
              Charged to the patient (charge mode) or accrued for settlement.
            </p>
            <label className="block text-xs font-medium text-slate-600 mb-1">Amount ($)</label>
            <div className="flex items-center gap-2">
              <div className="flex w-40 items-stretch">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-100 px-2.5 text-sm font-medium text-slate-500">
                  $
                </span>
                <AdminLockedFieldset>
                  <Input
                    type="number"
                    min={minPrice > 0 ? String(minPrice) : '0'}
                    step="0.01"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    className="rounded-l-none text-right"
                  />
                </AdminLockedFieldset>
              </div>
              {!locked && (
                <Button
                  size="sm"
                  onClick={() => savePrice.mutate(Number(priceInput))}
                  disabled={!priceDirty || belowMin || savePrice.isPending}
                >
                  {savePrice.isPending ? 'Saving…' : 'Save'}
                </Button>
              )}
            </div>
            {minPrice > 0 && (
              <p className={`mt-1.5 text-[11px] ${belowMin ? 'text-red-600' : 'text-slate-400'}`}>
                Minimum set by Prospera: {usd(minPrice)}.{belowMin ? ' Price must be at or above this.' : ''}
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Checkout mode */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Checkout</h3>
          <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
            {/* Locked: a native checkbox has no field lock, so it stays
                controlled and ignores the change (React restores `checked`). */}
            <input
              type="checkbox"
              checked={enforced}
              aria-readonly={locked || undefined}
              onChange={(e) => {
                if (locked) return;
                setEnforced(e.target.checked);
                saveEnforced.mutate(e.target.checked);
              }}
            />
            Require payment before creating the request
          </label>
          <p className="mt-2 text-[11px] text-slate-400">
            On — staff must collect payment first; the request is only sent after it's paid (nothing is created if
            they cancel). Off — the request is created immediately and the charge is collected after (today's
            behavior).
          </p>
        </CardBody>
      </Card>

      {/* Billing */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Billing</h3>
          {settings && locked ? (
            // Locked: RadioGroup has no read-only mode, so show the chosen mode.
            <p className="mt-1 text-sm text-slate-800">
              {BILLING_MODE_LABELS[settings.billingMode] ?? settings.billingMode}
            </p>
          ) : settings ? (
            <RadioGroup
              name={`${KEY}-billing`}
              options={billingOptions}
              value={settings.billingMode}
              onChange={(v) => saveMode.mutate(v)}
            />
          ) : (
            <Spinner size="sm" />
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            “Charge the patient” collects the fee at order time. “Log &amp; settle monthly” logs the order; the
            account reconciles the payout to the vendor for executed visits.
          </p>
        </CardBody>
      </Card>

      {/* Banking (ACH). Shown only for integrations that enroll with a direct
          debit — the clinic's own record, editable by the account that signed
          the authorization. Full numbers are never fetched; last-4 is all the
          API returns. */}
      {requiresOnboarding && onboarding?.onboarded && (
        <Card>
          <CardBody>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">Banking information</h3>
                <p className="mt-1 text-[11px] text-slate-400">
                  The account {vendorName} debits for orders placed by this clinic.
                </p>
                <div className="mt-3 text-sm text-slate-700">
                  {banking?.exists && banking.accountLast4 ? (
                    <>
                      <span className="font-medium">{banking.bankName || 'Bank account'}</span>
                      <span className="text-slate-400"> · </span>
                      <span className="capitalize">{banking.accountType || 'checking'}</span>
                      <span className="text-slate-400"> ••••{banking.accountLast4}</span>
                      {banking.routingLast4 && (
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          Routing ••••{banking.routingLast4}
                          {banking.updatedAt
                            ? ` · updated ${new Date(banking.updatedAt).toLocaleDateString()}`
                            : ''}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-400">No bank account on file.</span>
                  )}
                </div>
              </div>
              {!locked && (
                <button
                  type="button"
                  onClick={() => setBankingOpen(true)}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Update banking details
                </button>
              )}
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Stored encrypted. We show only the last four digits.
            </p>
          </CardBody>
        </Card>
      )}

      {!locked && (
        <AxiomBankingModal
          integrationKey={onboardingScope}
          vendorName={vendorName}
          open={bankingOpen}
          onClose={() => setBankingOpen(false)}
          onSaved={({ synced, syncError }) => {
            // A local save with a failed vendor sync is not a success: the next
            // debit still goes to the old account until the retry lands.
            if (synced === false && syncError) toast.warning(syncError);
            else toast.success('Banking details updated.');
          }}
        />
      )}

      {/* Covered-by-membership */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Covered by membership</h3>
          <p className="text-[11px] text-slate-400 mb-2">
            Patients with an active membership selected here have their visit covered by the account — the
            patient isn't charged, regardless of the billing mode above.
          </p>
          <AdminLockedFieldset>
            <SearchableMultiSelect
              options={membershipOptions}
              selectedValues={entitledLocal}
              onSelectionChange={(values) => {
                if (locked) return;
                setEntitledLocal(values);
                saveEntitled.mutate(values.map((v) => Number(v)));
              }}
              placeholder="No memberships — none will be covered"
              emptyMessage="No membership templates"
            />
          </AdminLockedFieldset>
        </CardBody>
      </Card>

      {/* Reconciliation */}
      <Card>
        <CardBody>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Month-end reconciliation</h3>
            {recon && <Badge variant="info">{recon.upcomingVisits} upcoming</Badge>}
          </div>
          {reconLoading ? (
            <div className="flex justify-center py-8"><Spinner size="sm" /></div>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-4 gap-3 text-center">
                <Stat label="Visits (6 mo)" value={String(totals.visits)} />
                <Stat label="Collected" value={usd(totals.collected)} />
                <Stat label={`Owed to ${vendorName}`} value={usd(totals.owed)} />
                <Stat label="Profit" value={usd(totals.collected - totals.owed)} />
              </div>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={recon?.months ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => '$' + v} />
                    <Tooltip formatter={(v: number) => usd(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="collected"    name="Collected from patient" fill="#22c55e" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="owedToVendor" name={`Owed to ${vendorName}`} fill="#ef4444" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="ourProfit"    name="Profit"                 fill="#6366f1" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
      <div className="text-sm font-semibold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}
